/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import styled from 'styled-components';
import ReactInputMask from 'react-input-mask';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';

import { useEffect, useState } from 'react';
import {
  InputAutocompletePlaces,
  InputTextWithLabel,
  InputBaseContainer,
  InputSelectWithLabel,
  ButtonWithLoader,
  CheckboxWithLabel,
} from '../molecules';
import { FormServerError } from '../types';
import {
  JurisdictionType,
  JurisdictionTableType,
} from '../api/jurisdiction';
import transformServerErrorsToForm from '../utils/transformServerErrorsToForm';
import {
  FranchiseType,
  getFranchiseList,
} from '../api/franchise';
import isYupFieldRequired from '../utils/isYupFieldRequired';
import { emailRegex, phoneRegex, urlRegex, zipCode } from '../regex';
import { GET_ALL_QUERY_PARAMS } from '../constants';
import useUserRole from '../hooks/useUserRole';
import JurisdictionFormCounty from './JurisdictionFormCounty';
import { statesItems } from '../modules/WikiModule/wikiutil';

const labels = {
  name: 'Jurisdiction name',
  phoneNumber: 'Phone',
  building: 'Building Official Name',
  email: 'Email',
  address: 'Address',
  county: 'County',
  franchise: 'Branch',
  street: 'Street',
  city: 'City',
  state: 'State',
  zipCode: 'Zip',
  isDefault: 'Set as default in new Branch',
  jurisdictionWebsite: 'Jurisdiction Website',
};

const schema = yup.object().shape({
  name: yup.string().required().label(labels.name),
  phoneNumber: yup
    .string()
    .matches(
      phoneRegex,
      'Invalid phone number. The phone number must be in format 2XX-XXX-XXXX',
    )
    .required()
    .label(labels.phoneNumber),
  building: yup.string().required().label(labels.building),
  jurisdictionWebsite: yup
  .string()
  .matches(urlRegex, 'Enter a valid Url starting with http:// or https://')
  .required()
  .label(labels.jurisdictionWebsite),
  email: yup
    .string()
    .matches(emailRegex, 'Enter a valid email address')
    .required()
    .label(labels.email),
  county: yup.object().shape({
    id: yup.number(),
    name: yup.string(),
    state: yup.string(),
    websitePropertyAppraiser: yup.string(),
  }).required().label(labels.county),
  address: yup.string().required().label(labels.address),
  franchise: yup
    .array()
    .of(
      yup.object().shape({
        value: yup.string(),
        label: yup.string(),
      }),
    )
    .typeError('Select a franchise from the list')
    .nullable()
    .label(labels.franchise),
  street: yup.string().label(labels.street),
  city: yup.string().label(labels.city),
  zipCode: yup
    .string()
    .matches(
      zipCode,
      'Invalid Code number. The code number must be in format 55555 or 55555-5555',
    )
    .label(labels.zipCode),
  isDefault: yup.boolean().label(labels.isDefault),
});

const FL = { id: 'FL', name: 'Florida', title: 'Florida', label: 'Florida', value: 'FL' };

const JurisdictionForm = ({
  onSubmit,
  initialValues,
  modalError,
  addButtonText = 'Add new item',
  updateButtonText = 'Update',
  initialErrors,
}: Props) => {
  const {
    register,
    handleSubmit,
    control,
    errors,
    setError,
    setValue,
    getValues,
    formState,
    watch,
  } = useForm<JurisdictionTableType>({
    resolver: yupResolver(schema),
    defaultValues: initialValues || ({} as any),
  });
  const { isSuperAdmin } = useUserRole();

  const isDeleted = initialValues?.isDeleted;
  const [stateValue, setStateValue] = useState<any>(FL);
  const [isSubmitting, setIsSubmitting] = React.useState<
    boolean
  >(false);

  React.useEffect(() => {
    if (
      formState.isSubmitting &&
      !Object.keys(errors).length
    ) {
      setIsSubmitting(formState.isSubmitting);
    }
  }, [formState]);

  const [isLoading, setIsLoading] = React.useState(false);
  const [emailBlurError, setEmailBlurError] = React.useState('');
  const [
    franchisesData,
    setFranchisesData,
  ] = React.useState([]);

  const handlePlaceSelect = (address, isSelect) => {
    if (isSelect) {
      return Object.entries(address).map(([key, value]) => {
        return setValue(key, value);
      });
    }
    return setValue('address', address);
  };

  React.useEffect(() => {
    setIsLoading(true);

    getFranchiseList({
      ...GET_ALL_QUERY_PARAMS,
      ordering: 'name',
    })
      .then(response =>
        setFranchisesData(response.data.results))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  React.useEffect(() => {
    if (initialValues?.id) {
      if (franchisesData.length) {
        setValue(
          'franchise',
          franchisesData.filter(item =>
            initialValues.franchise.includes(item.id)),
        );
      }
    }
  }, [franchisesData]);

  React.useEffect(() => {
    if (initialErrors) {
      transformServerErrorsToForm<JurisdictionTableType>(
        initialErrors,
      ).forEach(({ name, types }) =>
        setError(name, { types }));
    }
  }, [initialErrors]);

  React.useEffect(() => {
    if (Object.keys(errors).length) {
      setIsSubmitting(false);
    }
  }, [errors, initialErrors]);

  useEffect(() => {
    if (initialValues?.state) {
      const _state = statesItems.find(s => s.id === initialValues.state);
      setValue('state', _state);
    }
  }, [initialValues]);
  const onStateChanged = e => {
    setStateValue(e.id);
  };
  const middleware = (
    submitFn: (data: JurisdictionType) => void,
  ) => (data: JurisdictionTableType) => {
    let _state = data?.state?.id || data.state;
    if (!_state) {
      _state = 'FL';
    }
    return submitFn({
      ...data,
      state: _state,
      id: initialValues?.id,
      isDeleted: initialValues?.isDeleted,
      county: data.county.name,
      websitePropertyAppraiser: data.county.websitePropertyAppraiser,
      franchise: data.franchise
        ? (data.franchise as FranchiseType[])
            ?.filter(item => item.id)
            .map(item => item.id)
        : [],
    });
  };

  return (
    <Container
      disabled={isDeleted || (isSubmitting && !modalError)}
    >
      <FormContainer
        autoComplete="off"
        aria-autocomplete="none"
        onSubmit={handleSubmit(middleware(onSubmit), err => console.log(err))}
      >
        <Columns>
          <Column>
            <InputBaseContainer>
              <InputTextWithLabel
                name="name"
                error={errors.name}
                inputRef={register}
                label={labels.name}
                InputProps={{
                  readOnly: !isSuperAdmin,
                }}
                isRequired={isYupFieldRequired('name', schema)}
              />
            </InputBaseContainer>
            <InputBaseContainer>
              <Controller
                control={control}
                defaultValue=""
                name="phoneNumber"
                render={props => (
                  <ReactInputMask
                    mask="999-999-9999"
                    maskChar=" "
                    {...props}
                  >
                    {() => (
                      <InputTextWithLabel
                        error={errors.phoneNumber}
                        label={labels.phoneNumber}
                        isRequired={isYupFieldRequired(
                          'phoneNumber',
                          schema,
                        )}
                      />
                    )}
                  </ReactInputMask>
                )}
              />
              <InputTextWithLabel
                name="email"
                type="email"
                error={errors.email || (emailBlurError ? { message: emailBlurError } : undefined)}
                inputRef={register}
                label={labels.email}
                placeholder="user@example.com"
                isRequired={isYupFieldRequired('email', schema)}
                onBlur={e => {
                  const val = e.target.value;
                  if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
                    setEmailBlurError('Please enter a valid email address');
                  } else {
                    setEmailBlurError('');
                  }
                }}
                onChange={() => { if (emailBlurError) setEmailBlurError(''); }}
              />
            </InputBaseContainer>
            <InputBaseContainer>
              <InputTextWithLabel
                name="building"
                error={errors.building}
                inputRef={register}
                label={labels.building}
                isRequired={isYupFieldRequired(
                  'building',
                  schema,
                )}
              />
            </InputBaseContainer>
            <InputBaseContainer>
              <InputTextWithLabel
                name="jurisdictionWebsite"
                error={errors.jurisdictionWebsite}
                inputRef={register}
                label={labels.jurisdictionWebsite}
                isRequired={isYupFieldRequired('jurisdictionWebsite', schema)}
              />
              <Controller
                name="franchise"
                defaultValue=""
                control={control}
                render={props => (
                  <InputSelectWithLabel
                    isMulti
                    selectAllActive
                    placeholder=""
                    menuPlacement="top"
                    isLoading={isLoading}
                    label={labels.franchise}
                    error={errors.franchise}
                    isDisabled={isLoading || !isSuperAdmin}
                    getOptionValue={(item: any) =>
                      item.id || Math.random()
                    }
                    getOptionLabel={(item: any) => item.name}
                    isRequired={isYupFieldRequired(
                      'franchise',
                      schema,
                    )}
                    options={franchisesData}
                    {...props}
                  />
                )}
              />
            </InputBaseContainer>
            {isSuperAdmin && (
              <InputBaseContainer>
                <Controller
                  name="isDefault"
                  control={control}
                  valueName="checked"
                  defaultValue={
                    initialValues?.isDefault ?? true
                  }
                  render={({ onChange, onBlur, value }) => (
                    <CheckboxWithLabel
                      error={errors.isDefault}
                      label={labels.isDefault}
                      checked={value}
                      onBlur={onBlur}
                      onChange={(e, flag) => onChange(flag)}
                    />
                  )}
                />
              </InputBaseContainer>
            )}
          </Column>
          <Column>
            <InputBaseContainer>
              <Controller
                name="address"
                defaultValue=""
                control={control}
                render={props => (
                  <InputAutocompletePlaces
                    error={errors.address}
                    label={labels.address}
                    handleFieldChange={handlePlaceSelect}
                    isRequired={isYupFieldRequired(
                      'address',
                      schema,
                    )}
                    {...props}
                  />
                )}
              />
            </InputBaseContainer>
            <InputBaseContainer>
              <InputTextWithLabel
                name="street"
                error={errors.street}
                inputRef={register}
                label={labels.street}
                isRequired={isYupFieldRequired(
                  'street',
                  schema,
                )}
              />
              <InputTextWithLabel
                name="city"
                error={errors.city}
                inputRef={register}
                label={labels.city}
                isRequired={isYupFieldRequired('city', schema)}
              />
            </InputBaseContainer>
            <InputBaseContainer>
              <Controller
                name="state"
                control={control}
                render={props => (
                  <InputSelectWithLabel
                    selectAllActive
                    placeholder=""
                    menuPlacement="top"
                    isLoading={isLoading}
                    label="State"
                    isDisabled={false}
                    getOptionValue={(item: any) => item.id || Math.random()}
                    getOptionLabel={(item: any) => item.name}
                    options={statesItems}
                    onChange={onStateChanged}
                    defaultValue={FL}
                    {...props}
                  />
                )}
              />

              <Controller
                name="zipCode"
                defaultValue=""
                control={control}
                render={({ onChange, onBlur, value }) => (
                  <InputTextWithLabel
                    name="zipCode"
                    label={labels.zipCode}
                    error={errors.zipCode}
                    isRequired={isYupFieldRequired(
                      'zipCode',
                      schema,
                    )}
                    value={value}
                    onBlur={onBlur}
                    onChange={e => {
                      const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 9);
                      let formatted = digits;
                      if (digits.length > 5) {
                        formatted = `${digits.slice(0, 5)}-${digits.slice(5)}`;
                      }
                      onChange(formatted);
                    }}
                    inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                    placeholder="55555 or 55555-5555"
                  />
                )}
              />
            </InputBaseContainer>

            <JurisdictionFormCounty
              openTop
              control={control}
              errors={errors}
              getValues={getValues}
              setValue={setValue}
              watch={watch}
            />
          </Column>
        </Columns>
        {!isDeleted && (
          <ButtonWithLoader
            disabled={isSubmitting && !modalError}
            loading={isSubmitting && !modalError}
            type="submit"
          >
            {initialValues
              ? updateButtonText
              : addButtonText}
          </ButtonWithLoader>
        )}
      </FormContainer>
    </Container>
  );
};

const Container = styled.fieldset`
  display: block;
  margin: 0;
  padding: 0;
  width: 100%;
  border: none;
  text-align: center;
`;

const FormContainer = styled.form`
  width: 100%;

  .MuiButton-root {
    margin-top: 24px;
  }
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 0 24px;
  width: 100%;
  box-sizing: border-box;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 0 8px;
  box-sizing: border-box;
`;

interface Props {
  addButtonText?: string;
  updateButtonText?: string;
  initialValues?: JurisdictionTableType;
  onSubmit: (data: JurisdictionType) => void;
  initialErrors?: FormServerError<JurisdictionTableType>;
  modalError: boolean;
}

export type JurisdictionFields = Omit<
  JurisdictionTableType,
  'id' | 'isDeleted' | 'franchiseId'
> &
  Pick<JurisdictionType, 'franchise'>;

export default JurisdictionForm;
